import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, ExternalLink } from "lucide-react";
import CategoryHero from "./CategoryHero";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from './NewsTicker';
import SocialLinks, { SOCIAL_FIELDS } from './SocialLinks';

const NONPROFIT_FIELDS: FieldDef[] = [
  { key: "name", label: "Name", required: true },
  { key: "description", label: "Description" },
  { key: "website", label: "Website URL", type: "url", placeholder: "https://…" },
  { key: "address", label: "Address", type: "textarea" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  ...SOCIAL_FIELDS,
];

export interface NonProfitEntry {
  name: string;
  description: string;
  website: string;
  address: string;
  image: string;
  instagram?: string;
}

export const NONPROFIT_ENTRIES: NonProfitEntry[] = [
  {
    name: "Dare2StopHunger",
    description: "Together We Can",
    website: "https://hungerstops.com/",
    address: "",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783433327423-b9jni5.png",
    instagram: "https://www.instagram.com/dare2stophunger/"
  },
  {
    name: "PRATHAM USA",
    description: "Change lives and transforms Communities",
    website: "https://prathamusa.org/",
    address: "",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782835470379-7cd1gq.png"
  },
  {
    name: "DFW Senior Citizen Samaj",
    description: "",
    website: "https://dfwscs.org/",
    address: "3119 Jubilee Trail, Dallas, TX 75229",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783432343213-sj0an.png"
  },
  {
    name: "Peoples Impact Network",
    description: "",
    website: "",
    address: "McDermott Rd, Suite 200, Plano, TX 75025",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783438655123-umtm3pjf.png"
  },
  {
    name: "Princeton Sanathana Dharama Foundation",
    description: "Preserving Sanathana Dharma",
    website: "",
    address: "",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784540677772-m6hp3.png",
    instagram: "https://www.instagram.com/princetonhinduorg/"
  }
];

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=600&h=400&fit=crop";

function mapsUrl(e: NonProfitEntry) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${e.name}, ${e.address}`)}`;
}

export function NonProfitCard({ entry, index }: { entry: NonProfitEntry; index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-md border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <div className="w-full aspect-[4/3] relative overflow-hidden bg-gray-100">
        <img
          src={entry.image || FALLBACK_IMAGE}
          alt={entry.name}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
          }}
        />
      </div>
      <div className="p-1.5 pb-0.5 flex-1 flex flex-col justify-between">
        <div>
          <h3 className="text-[10px] font-bold text-[#082f6d] leading-tight truncate">
            <a
              href={`/non-profit-org/${slugify(entry.name || String(index))}`}
              className="hover:underline"
            >
              {entry.name}
            </a>
          </h3>
          {entry.description && (
            <p className="text-[9px] text-gray-500 font-medium leading-snug mt-0.5">
              {entry.description}
            </p>
          )}
          {entry.address && (
            <div className="flex items-start gap-0.5 mt-0.5">
              <MapPin className="w-2.5 h-2.5 text-orange-500 shrink-0 mt-0.5" />
              <span className="text-[9px] text-gray-500 font-medium leading-snug truncate">
                {entry.address}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="px-1.5 pb-1.5 pt-0.5 flex flex-col gap-1">
        {entry.website && (
          <form action={entry.website} method="get" target="_top" className="w-full">
            <button
              type="submit"
              className="w-full flex items-center justify-center gap-0.5 bg-[#082f6d] hover:bg-[#062454] text-white text-[9px] font-semibold py-1 rounded-sm transition-colors duration-200 cursor-pointer"
            >
              <ExternalLink className="w-2.5 h-2.5" /> Visit Website
            </button>
          </form>
        )}
        <SocialLinks item={{ ...entry, website: undefined, websiteUrl: undefined, websiteLink: undefined, website_url: undefined }} variant="sleek" />
        {entry.address && entry.name !== "DFW Senior Citizen Samaj" && (
          <a
            href={mapsUrl(entry)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-0.5 bg-white border border-[#082f6d] text-[#082f6d] hover:bg-[#082f6d] hover:text-white text-[9px] font-semibold py-1 rounded-sm transition-colors duration-200 cursor-pointer"
          >
            <ExternalLink className="w-2.5 h-2.5" /> View Location
          </a>
        )}
      </div>
    </motion.div>
  );
}

export default function NonProfitOrg() {
  const [query, setQuery] = useState("");
  const mc = useManagedCollection<any>("non_profit_org", NONPROFIT_ENTRIES, (e, i) => slugify(e.name || String(i)));

  const filtered = mc.items.filter(
    (e) =>
      query.trim() === "" ||
      e.name.toLowerCase().includes(query.trim().toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white">
      <CategoryHero
        title="Non Profit Org"
        placeholder="Search non profit organizations…"
        backgroundImage="https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=1440&h=400&fit=crop"
        query={query}
        setQuery={setQuery}
      />
      <NewsTicker />

      <section
        className="bg-gray-50/40 py-8"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex flex-col lg:flex-row gap-6">
          <div className="flex-1 min-w-0">
            <h2 className="text-[16px] md:text-[18px] font-bold text-gray-900 mb-1">
              Non Profit Organizations
            </h2>
            <p className="text-[13px] text-gray-500 mb-5">
              Discover non profit organizations serving the Dallas-DFW community.
            </p>
            <AdminAddBar mc={mc} fields={NONPROFIT_FIELDS} label="Add Listing" />
            {filtered.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5">
                {filtered.map((e, i) => (
                  <AdminItem key={e._key} mc={mc} item={e} fields={NONPROFIT_FIELDS} title="Listing">
                    <NonProfitCard entry={e as any} index={i} />
                  </AdminItem>
                ))}
              </div>
            ) : (
              <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-8 text-center text-[13px] text-gray-500">
                No non profit organization listings yet.
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
